import { forwardRef } from 'react';

// Port từ app: PackageCardReceiptContent (screens/employee/ListBillScreen.js).
// Phiếu thẻ dịch vụ in được: thông tin từng thẻ + dịch vụ trong gói + buổi + tài chính.

const fmtVnd = (n) => `${Number(n || 0).toLocaleString('vi-VN', { maximumFractionDigits: 0 })}đ`;

const fmtDate = (iso) => {
    if (!iso) return 'Vô hạn';
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const fmtDateTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const p = (n) => String(n).padStart(2, '0');
    return `${p(d.getHours())}:${p(d.getMinutes())} ${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const fmtPhone = (phone) => {
    const s = (phone || '').toString().replace(/\D/g, '');
    if (s.length === 10) return `${s.slice(0, 4)} ${s.slice(4, 7)} ${s.slice(7)}`;
    return phone || '';
};

function Row({ label, value, bold }) {
    return (
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '2px 0' }}>
            <span style={{ fontSize: 13, color: '#000' }}>{label}</span>
            <span style={{ fontSize: 13, color: '#000', fontWeight: bold ? 700 : 400, textAlign: 'right' }}>
                {value}
            </span>
        </div>
    );
}

const Dashed = ({ my = 8 }) => (
    <div style={{ borderTop: '1px dashed #000', margin: `${my}px 0` }} />
);

const PackageCardReceipt = forwardRef(function PackageCardReceipt({ packages, bill, shopInfo }, ref) {
    const list = packages || [];
    return (
        <div
            ref={ref}
            style={{
                width: 320,
                margin: '0 auto',
                padding: '16px 14px',
                background: '#fff',
                color: '#000',
                fontFamily: "'Be Vietnam Pro', system-ui, sans-serif",
            }}
        >
            {/* Header shop */}
            {shopInfo && (
                <div style={{ textAlign: 'center', marginBottom: 6 }}>
                    <div style={{ fontSize: 19, fontWeight: 700, letterSpacing: 0.3 }}>
                        {shopInfo.nameShop || shopInfo.name}
                    </div>
                    {(shopInfo.phoneOne || shopInfo.phoneTwo) && (
                        <div style={{ fontSize: 13, marginTop: 2 }}>
                            {[shopInfo.phoneOne, shopInfo.phoneTwo].filter(Boolean).map(fmtPhone).join(' · ')}
                        </div>
                    )}
                    {shopInfo.address && (
                        <div style={{ fontSize: 13, marginTop: 1 }}>{shopInfo.address}</div>
                    )}
                </div>
            )}

            {/* Title */}
            <div style={{ textAlign: 'center', marginTop: 10, marginBottom: 4 }}>
                <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: 1.2 }}>PHIẾU THẺ DỊCH VỤ</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>
                    {bill?.code ? `HĐ: ${bill.code}` : ''}
                    {bill?.code && (bill?.checkoutTime || bill?.createdAt) ? '   ·   ' : ''}
                    {fmtDateTime(bill?.checkoutTime || bill?.createdAt || '')}
                </div>
            </div>

            <Dashed />

            {/* Khách hàng */}
            <Row label="Khách hàng" value={bill?.customer?.name || 'Khách lẻ'} bold />
            {bill?.customer?.phone && <Row label="SĐT" value={fmtPhone(bill.customer.phone)} />}

            {/* Từng thẻ */}
            {list.map((pkg, idx) => {
                const snap = pkg.templateSnapshot || {};
                const items = Array.isArray(snap.items) ? snap.items : [];
                const description = (snap.description || '').trim();
                const total = Number(pkg.totalSessions || 0);
                const bonus = Number(pkg.bonusSessions || 0);
                const used = Number(pkg.usedSessions || 0);
                const remaining = Math.max(0, total + bonus - used);

                return (
                    <div key={pkg.id ?? idx} style={{ marginTop: 10 }}>
                        <Dashed my={6} />
                        <div style={{ fontSize: 15, fontWeight: 700, marginTop: 4 }}>
                            {snap.name || pkg.package?.name || `Thẻ ${pkg.code}`}
                        </div>

                        <div style={{ marginTop: 8 }}>
                            <Row label="Mã thẻ" value={pkg.code} />
                            <Row label="Ngày mua" value={fmtDateTime(pkg.purchasedAt || '')} />
                            <Row label="Hiệu lực đến" value={pkg.expiredAt ? fmtDate(pkg.expiredAt) : 'Vô hạn'} />
                        </div>

                        {items.length > 0 && (
                            <div style={{ marginTop: 8 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                                    Dịch vụ trong gói:
                                </div>
                                {items.map((it, i) => (
                                    <div key={i} style={{ fontSize: 13, marginBottom: 2, paddingLeft: 6 }}>
                                        • {it.name}
                                        {it.quantity > 1 ? ` ×${it.quantity}/buổi` : ''}
                                        {it.isVirtual ? '  (Tặng kèm)' : ''}
                                    </div>
                                ))}
                            </div>
                        )}

                        {description && (
                            <div style={{ fontSize: 13, marginTop: 8, lineHeight: 1.5 }}>
                                <span style={{ fontWeight: 600 }}>Mô tả: </span>
                                {description}
                            </div>
                        )}

                        <Dashed />
                        <Row label="Tổng buổi" value={`${total}${bonus > 0 ? ` + ${bonus} tặng` : ''} buổi`} />
                        <Row label="Đã dùng" value={`${used} buổi`} />
                        <Row label="Còn lại" value={`${remaining} buổi`} bold />

                        <Dashed />
                        <Row label="Giá trị thẻ" value={fmtVnd(pkg.totalContractValue)} bold />
                        <Row label="Giá / buổi" value={fmtVnd(pkg.amountPerSession)} />
                        {pkg.sellerEmployee?.name && <Row label="NV bán" value={pkg.sellerEmployee.name} />}
                    </div>
                );
            })}

            <Dashed my={12} />
            <div style={{ textAlign: 'center', fontSize: 12, marginBottom: 2 }}>
                In lúc: {fmtDateTime(new Date().toISOString())}
            </div>
            <div style={{ textAlign: 'center', fontSize: 14, fontWeight: 700, marginTop: 4 }}>
                Cảm ơn quý khách!
            </div>
        </div>
    );
});

export default PackageCardReceipt;
